import React from 'react'
import { Container } from 'reactstrap'
import { Navigate, useParams } from 'react-router-dom'
import { useApps, type AppInfo } from '../contexts/AppsContext'
import { useDashboardData } from '../utils/useDashboardData'
import PageToolbar from './PageToolbar'
import ReportComponent from './ReportComponent'
import StatusCard from './StatusCard'
import SubTabs from './SubTabs'
import './ReportComponents.css'
import './DashboardComponent.css'

// Compact status for internal lower-priority apps: one card each, single environment, no infra rows.
// External services (e.g. NetSuite) only get a sub-tab
const AdditionalOverview: React.FC<{ apps: AppInfo[]; subNav: React.ReactNode }> = ({ apps, subNav }) => {
  const { summaries, health, ciStatus, loading, isSyncing, lastSync, error, refresh } = useDashboardData(
    'additional',
    false,
  )

  // Same container as the sub-tab report lists, so the tabs don't shift when switching
  return (
    <Container className="report-container">
      {subNav}
      <PageToolbar
        error={error}
        lastSync={lastSync}
        onSync={refresh}
        syncing={isSyncing}
        disabled={loading}
        syncLabel="Refresh"
      />
      {loading ? (
        <p>Loading dashboard...</p>
      ) : (
        <div className="additional-grid">
          {summaries
            .filter(product => apps.some(a => a.key === product.key))
            .map(product => {
              const app = apps.find(a => a.key === product.key)
              const envInfo = app?.envs[0]
              const productHealth = health.find(h => h.key === product.key)
              return (
                <StatusCard
                  key={product.key}
                  compact
                  label={product.label}
                  to={`/additional/${product.key}`}
                  envSummary={product.dev}
                  fe={productHealth?.fe.dev}
                  be={productHealth?.be.dev}
                  showFe={envInfo?.hasFe ?? false}
                  showBe={envInfo?.hasBe ?? false}
                  ci={app?.hasCi ? ciStatus.find(c => c.key === product.key) : undefined}
                />
              )
            })}
        </div>
      )}
    </Container>
  )
}

const AdditionalServicesPage: React.FC = () => {
  const { appKey } = useParams()
  const { apps, loading } = useApps()
  const additionalApps = apps.filter(a => a.tier === 'additional')
  const app = appKey ? additionalApps.find(a => a.key === appKey) : undefined

  if (loading) return null
  if (appKey && !app) return <Navigate to="/additional" replace />

  const subNav = (
    <SubTabs
      ariaLabel="Additional services"
      tabs={[
        { to: '/additional', label: 'Overview', end: true },
        ...additionalApps.map(a => ({ to: `/additional/${a.key}`, label: a.label })),
      ]}
    />
  )

  // key: remount per app so the report list resets its state when switching sub-tabs
  return app ? (
    <ReportComponent key={app.key} app={app} subNav={subNav} />
  ) : (
    <AdditionalOverview apps={additionalApps.filter(a => !a.external)} subNav={subNav} />
  )
}

export default AdditionalServicesPage
